import { createFileRoute, type SearchSchemaInput } from "@tanstack/react-router";
import { SeasonalClimateExperiment } from "@charphq/ui/components/experiments";
// Deep import rather than the `lib/experiments` barrel: `validateSearch` runs
// before the code-split component loads, so anything it reaches lands in the
// entry chunk. The barrel would drag the dataset modules in with it.
import {
  parseSeasonalClimateSearch,
  type SeasonalClimateSearch,
} from "@charphq/ui/lib/experiments/search";
import { useSeasonalClimate } from "../lib/useSeasonalClimate";

/**
 * Code-split automatically: `vite.config.ts` runs
 * `tanstackRouter({ autoCodeSplitting: true })`, so this component leaves the
 * main bundle without needing `createLazyFileRoute`.
 *
 * Like Live Network and the Climate Loop, this route does not use
 * `ExperimentRoutePage`: the wheel has a year selector, a pinned ring and a
 * synthetic-data escape hatch of its own, so it owns its search schema and
 * renders the experiment directly.
 *
 * **The real query is issued here, not inside the component.** `@charphq/ui`
 * does not depend on `@charphq/api-client`, and keeping it that way is what
 * lets the experiment render from a fixture in its tests — see ADR 0023.
 */
export const Route = createFileRoute("/experiments/seasonal-climate")({
  validateSearch: (search: Record<string, unknown> & SearchSchemaInput) =>
    parseSeasonalClimateSearch(search),
  component: RouteComponent,
});

function RouteComponent() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  // `?data=synthetic` opts out of the backend entirely, so the query is not even issued.
  const synthetic = search.data === "synthetic";
  const query = useSeasonalClimate();

  /**
   * Every control writes to the URL rather than to local state, which is what makes a projected
   * screen reproducible from its address. `replace: true` keeps a session of fiddling with filters
   * from burying the previous page under fifty history entries.
   *
   * Re-parsing rather than spreading guarantees a complete, valid object even if the previous URL
   * was hand-edited, and drops keys a patch sets to `undefined` — which is how unpinning a ring
   * removes `?highlight=` rather than leaving it empty.
   */
  const onSearchChange = (patch: Partial<SeasonalClimateSearch>) =>
    void navigate({
      // Spread into a fresh literal: the router's reducer wants a type with an implicit index
      // signature, which an interface does not have.
      search: (previous: Record<string, unknown>) => ({
        ...parseSeasonalClimateSearch({ ...previous, ...patch }),
      }),
      replace: true,
    });

  return (
    <SeasonalClimateExperiment
      search={search}
      onSearchChange={onSearchChange}
      query={synthetic ? undefined : query}
    />
  );
}
